/**
 * One value of a column added from the Analyst rail: the formula in words,
 * the inputs it was worked out from — each with the workbook cell it was read
 * from — and where the house sits in its plat. The column can be removed
 * from here.
 */

import { ExternalLink, Trash2, X } from "lucide-react";
import React, { useState } from "react";
import type { AddedColumn, Portfolio } from "../../services/portfolioDataService";
import {
	ORIGIN_LABEL,
	ORIGIN_PILL,
	addedAggregate,
	addedValue,
	formatAdded,
	formatMoney,
	inWords,
	lineItemLabel,
	workbookCellHref,
} from "./portfolioModel";

export interface AddedCellPanelProps {
	portfolio: Portfolio;
	column: AddedColumn;
	houseId: string;
	year: number;
	onClose: () => void;
	onAsk: () => void;
	onRemove: (columnId: string) => Promise<void>;
}

const SECTION_HEADING = "text-xs font-semibold uppercase tracking-[0.06em] text-[#6B7280]";

const formatInput = (value: number | string | null, name: string): string => {
	if (typeof value !== "number") return value === null ? "—" : String(value);
	return name.endsWith("_sqft") || name === "year_built"
		? value.toLocaleString("en-US")
		: `$${formatMoney(value)}`;
};

const AddedCellPanel: React.FC<AddedCellPanelProps> = ({
	portfolio,
	column,
	houseId,
	year,
	onClose,
	onAsk,
	onRemove,
}) => {
	const [removing, setRemoving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const cell = addedValue(column, houseId, year);
	const house = portfolio.tables.house.find((h) => h.house_id === houseId);
	const platId = String(house?.plat_id ?? cell?.plat_id ?? "");
	const platHouses = portfolio.tables.house.filter((h) => h.plat_id === platId).map((h) => h.house_id);
	const platValues = column.values
		.filter((v) => v.year === year && v.plat_id === platId && v.value !== null)
		.map((v) => v.value as number)
		.sort((a, b) => b - a);
	const rank = cell?.value != null ? platValues.indexOf(cell.value) + 1 : 0;
	const platFigure = addedAggregate(column, platHouses, year);

	const remove = async () => {
		setRemoving(true);
		setError(null);
		try {
			await onRemove(column.id);
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : "Could not remove the column.");
			setRemoving(false);
		}
	};

	return (
		<aside
			aria-label="About this figure"
			className="flex w-[420px] flex-shrink-0 flex-col border-l border-gray-200 bg-white shadow-[-12px_0_32px_rgba(17,24,39,0.06)]"
		>
			<div className="flex items-start gap-3 border-b border-[#f1f2f4] px-[22px] pb-4 pt-5">
				<div className="min-w-0 flex-1">
					<p className="text-xs text-[#6B7280]">
						{houseId}
						{house?.address ? ` · ${String(house.address)}` : ""}
					</p>
					<h2 className="mt-1 break-words text-xl font-semibold text-[#111111]">
						{column.label}, {year}
					</h2>
				</div>
				<button
					type="button"
					onClick={onClose}
					aria-label="Close figure"
					className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-[#5B6472] hover:bg-gray-100"
				>
					<X className="h-[18px] w-[18px]" aria-hidden />
				</button>
			</div>

			<div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-y-auto px-[22px] py-[18px]">
				<div className="flex flex-wrap items-baseline gap-3">
					<span className="text-[28px] font-semibold tabular-nums text-[#111111]">
						{formatAdded(cell?.value ?? null, column)}
					</span>
					<span className="text-[13px] text-[#6B7280]">{column.unit}</span>
					<span className={`inline-flex h-[22px] items-center rounded-full px-2 text-[11.5px] font-semibold ${ORIGIN_PILL.worked}`}>
						{ORIGIN_LABEL.worked} · added in chat
					</span>
				</div>
				{cell?.note && (
					<p className="rounded-lg border border-[#fedf89] bg-[#fffaeb] px-3 py-2 text-[12.5px] text-[#7a2e0e]">
						Blank for this house: {inWords(cell.note)}.
					</p>
				)}

				{column.definition && (
					<section className="flex flex-col gap-2">
						<h3 className={SECTION_HEADING}>In plain words</h3>
						<p className="text-[15px] leading-relaxed text-[#1F2937]">{column.definition}</p>
					</section>
				)}

				<section className="flex flex-col gap-2">
					<h3 className={SECTION_HEADING}>How it's worked out</h3>
					<div className="rounded-[10px] border border-[#eceae6] bg-[#f7f7f6] px-3 py-2.5 text-[13px] text-[#1F2937]">
						{inWords(column.formula)}
					</div>
					<dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-2 text-[13px]">
						{(cell?.inputs ?? []).map((input) => {
							const href =
								input.sheet && input.cell && input.source_file
									? workbookCellHref(portfolio, {
											source_file: input.source_file,
											sheet: input.sheet,
											cell: input.cell,
										})
									: null;
							return (
								<React.Fragment key={input.name}>
									<dt className="text-[#5B6472]">
										{lineItemLabel(input.name)}{" "}
										{input.basis === "house_list" ? (
											<span className="text-[11.5px] text-[#9a9ea3]">house list</span>
										) : href ? (
											<a
												href={href}
												target="_blank"
												rel="noreferrer"
												className="inline-flex items-center gap-0.5 font-mono text-[11.5px] text-[#004C4B] underline underline-offset-2"
											>
												{input.sheet}!{input.cell}
												<ExternalLink className="h-3 w-3" aria-hidden />
											</a>
										) : (
											<span className="font-mono text-[11.5px] text-[#9a9ea3]">
												{input.sheet ? `${input.sheet}!${input.cell}` : ""}
											</span>
										)}
									</dt>
									<dd className="text-right tabular-nums text-[#1F2937]">
										{formatInput(input.value, input.name)}
									</dd>
								</React.Fragment>
							);
						})}
					</dl>
				</section>

				{platValues.length > 1 && (
					<section className="flex flex-col gap-2">
						<h3 className={SECTION_HEADING}>Next to the rest of {String(house?.plat_name ?? platId)}</h3>
						<dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1.5 text-[13px]">
							{platFigure !== null && (
								<>
									<dt className="text-[#5B6472]">
										{column.aggregate === "mean" ? "Average house" : "Plat total"}
									</dt>
									<dd className="text-right tabular-nums text-[#1F2937]">{formatAdded(platFigure, column)}</dd>
								</>
							)}
							{rank > 0 && (
								<>
									<dt className="text-[#5B6472]">Rank, highest first</dt>
									<dd className="text-right tabular-nums text-[#1F2937]">
										{rank} of {platValues.length}
									</dd>
								</>
							)}
						</dl>
					</section>
				)}

				<p className="text-xs text-[#6B7280]">
					Added from chat
					{column.added_at ? ` on ${new Date(column.added_at).toLocaleDateString("en-US", { day: "numeric", month: "short" })}` : ""}.
					Worked out again from your tables every time this page loads.
				</p>
				{error && <p className="text-xs text-[#b42318]">{error}</p>}

				<div className="mt-auto flex flex-wrap gap-2">
					<button
						type="button"
						onClick={onAsk}
						className="inline-flex h-9 items-center rounded-lg border border-[#99CFCD] bg-white px-3.5 text-sm font-medium text-[#004C4B] hover:bg-[#f6fbfb]"
					>
						Ask about this figure
					</button>
					<button
						type="button"
						onClick={remove}
						disabled={removing}
						className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3.5 text-sm font-medium text-[#5B6472] hover:bg-gray-50 disabled:opacity-60"
					>
						<Trash2 className="h-4 w-4" aria-hidden />
						{removing ? "Removing…" : "Remove column"}
					</button>
				</div>
			</div>
		</aside>
	);
};

export default AddedCellPanel;
